// Copyright 2019 Fowlit. All rights reserved.
//
// Use of this source code is governed by the MIT license that can be found in
// the LICENSE.md file.

// SearchBar component with dropdopwn list.

import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Colors, Fonts, Dimensions, Media } from 'fowlit';
import { useComponentVisible } from 'Hooks';

const selectHeight = '70px';
const itemHeight = '50px';

const Container = styled.div`
  user-select: none;
  grid-area: type;
  position: relative;
  background: ${props => props.theme.searchbarBackground};
  border-top-left-radius: ${Dimensions.radius};
  border-bottom-left-radius: ${props => (props.active ? 0 : Dimensions.radius)};
  display: grid;
  grid-template-columns: ${selectHeight} 1fr;
  grid-template-rows: ${selectHeight} 1fr;
  grid-template-areas: 'icon label' 'dropdown dropdown';
  border: 2px solid ${props => props.theme.searchbarBorder};
  @media (max-width: ${Media.phone}) {
    border-bottom-left-radius: 0;
    border-top-right-radius: ${Dimensions.radius};
  }
`;

const Icon = styled.i`
  grid-area: icon;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${Fonts.sizeTiny};
  color: ${props => (props.active ? props.theme.searchbarActive : props.theme.searchbarColor)};
  transition: 200ms ease-in-out;
`;

const Label = styled.div`
  grid-area: label;
  color: ${props => props.theme.searchbarColor};
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: ${Fonts.text};
  font-size: ${Fonts.sizeSuperTiny};
  padding: 0 30px 0 0;
  cursor: pointer;

  i {
    transform: ${props => (props.active ? `rotateZ(180deg)` : `none`)};
    transition: 200ms ease-in-out;
    font-size: ${Fonts.sizeTiny};
  }
`;

const Dropdown = styled.div`
  background: ${props => props.theme.searchbarBackground};
  display: ${props => (props.active ? 'block' : 'none')};
  position: absolute;
  top: calc(${selectHeight} - 2px);
  left: -2px;
  right: -2px;
  box-shadow: 1px 5px 5px -7px ${Colors.black};
  border-bottom-left-radius: ${Dimensions.radius};
  border-bottom-right-radius: ${Dimensions.radius};
  border-left: 2px solid ${props => props.theme.searchbarBorder};
  border-right: 2px solid ${props => props.theme.searchbarBorder};
  border-bottom: 2px solid ${props => props.theme.searchbarBorder};

  @media (max-width: ${Media.phone}) {
    grid-area: dropdown;
    top: -2px;
  }
`;

const Item = styled.div`
  line-height: ${itemHeight};
  padding: 0 30px;
  font-size: ${Fonts.sizeSuperTiny};
  font-family: ${Fonts.text};
  background: ${props => (props.selected ? props.theme.searchbarHover : 'none')};
  color: ${props => props.theme.searchbarColor};
  cursor: pointer;
  display: flex;
  justify-content: space-between;

  &:hover {
    background: ${props => props.theme.searchbarHover};
  }
  &:last-of-type {
    border-bottom-left-radius: ${Dimensions.radius};
    border-bottom-right-radius: ${Dimensions.radius};
  }
`;

const Select = ({ label, list, value, setValue }) => {
  const { ref, visible, setVisible } = useComponentVisible(false);

  return (
    <Container active={visible} ref={ref}>
      <Icon active={visible || value.type !== ''} className="icon-home" />
      <Label active={visible} onClick={() => setVisible(v => !v)}>
        {value.type === '' ? label : value.label}
        <i className="icon-angle-down" />
      </Label>
      <Dropdown active={visible}>
        {list.map(e => (
          <Item
            key={e.type}
            selected={e.type === value.type}
            onClick={() => {
              if (e.type !== value.type) {
                setValue(e);
                setVisible(false);
              } else {
                setValue({ type: '', label: '' });
              }
            }}
          >
            {e.label}
            <i className={e.type === value.type ? 'icon-cancel-circled' : ''} />
          </Item>
        ))}
      </Dropdown>
    </Container>
  );
};

Select.propTypes = {
  label: PropTypes.string.isRequired,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  ).isRequired,
  value: PropTypes.shape({
    type: PropTypes.string,
    label: PropTypes.string
  }).isRequired,
  setValue: PropTypes.func.isRequired
};

export default Select;
